import type {
  HubExampleStreamDefinitionType,
  HubExampleStreamViewTemplates,
  HubMinMaxCardinality,
  HubViewTemplate,
} from '@robovision/hub'
import { getCameraConfigurationDefinitionPredefinedName } from './CameraConfiguration'


export interface MinMaxCardinalityParams {
  max?: number;
  min?: number;
}

export class MinMaxCardinality {
  public min?: number
  public max?: number

  constructor (params: MinMaxCardinalityParams) {
    this.min = params.min
    this.max = params.max
  }

  public static fromResponse (data: HubMinMaxCardinality): MinMaxCardinality {
    return new MinMaxCardinality({
      max: data.max,
      min: data.min,
    })
  }
}

export interface ViewTemplateParams {
  cardinality: number | MinMaxCardinality;
  viewNameTemplate: string;
  viewPathTemplate: string;
}

export class ViewTemplate {
  public cardinality: number | MinMaxCardinality
  public viewNameTemplate: string
  public viewPathTemplate: string

  constructor (params: ViewTemplateParams) {
    this.cardinality = params.cardinality
    this.viewNameTemplate = params.viewNameTemplate
    this.viewPathTemplate = params.viewPathTemplate
  }

  public static fromResponse (data: HubViewTemplate): ViewTemplate {
    const cardinality = typeof data.cardinality === 'number'
      ? data.cardinality
      : MinMaxCardinality.fromResponse(data.cardinality)

    return new ViewTemplate({
      cardinality,
      viewNameTemplate: data.viewNameTemplate,
      viewPathTemplate: data.viewPathTemplate,
    })
  }

  public get isFixedCardinality (): boolean {
    return typeof this.cardinality === 'number'
  }

  public get minInstances (): number {
    if (typeof this.cardinality === 'number') {
      return this.cardinality
    }
    return this.cardinality.min ?? 0
  }

  public get maxInstances (): number | undefined {
    if (typeof this.cardinality === 'number') {
      return this.cardinality
    }
    return this.cardinality.max
  }
}

export interface ExampleStreamViewTemplatesParams {
  definitionId: string;
  definitionName: string;
  definitionType: HubExampleStreamDefinitionType;
  viewTemplates: ViewTemplate[];
}

export class ExampleStreamViewTemplates {
  public definitionId: string
  public definitionName: string
  public definitionType: HubExampleStreamDefinitionType
  public viewTemplates: ViewTemplate[]

  constructor (params: ExampleStreamViewTemplatesParams) {
    this.definitionId = params.definitionId
    this.definitionName = params.definitionName
    this.definitionType = params.definitionType
    this.viewTemplates = params.viewTemplates
  }

  public static fromResponse (data: HubExampleStreamViewTemplates): ExampleStreamViewTemplates {
    return new ExampleStreamViewTemplates({
      definitionId: data.definitionId,
      definitionName: getCameraConfigurationDefinitionPredefinedName(data.definitionId) ?? data.definitionName,
      definitionType: data.definitionType as HubExampleStreamDefinitionType,
      viewTemplates: data.viewTemplates.map(ViewTemplate.fromResponse),
    })
  }
}
